import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import Tree from '../../shared/ui/Tree'
import globalNotification from 'live/services/global-notification'
import { removeDuplicatesBy } from '../../utils/utils'
import useScrollYWithSelector from './useDashboardScroll'

export type FilterOption = {
    title: string
    key: string
}

interface OptionRowItemProps {
    options: FilterOption[]
    filterApplyed?: string[]
    apply?: (checkeds: string[]) => void
    isSmall: boolean
    topPosition?: number
    warning?: {
        title: string
        subtitle: string
    }
}
const Dropdown = (props: OptionRowItemProps): JSX.Element => {
    const { options, apply, filterApplyed = [] } = props
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [selectAll, setSelectAll] = useState<boolean>(false)
    const [checked, setChecked] = useState<string[]>([])
    // Add this state to show the selected items in the dropdown to fix to just edit the name show when click in applyed
    const [selectAllApplyed, setSelectAllApplyed] = useState<boolean>(false)
    const [checkedApplyed, setCheckedApplyed] = useState<string[]>([])

    const wrapperRef = useRef(null)
    useOutsideAlerter(wrapperRef)

    const scrollPosition = useScrollYWithSelector('.dashboard-content')
    const buttonRef = useRef(null)
    const boundingClientRect = buttonRef?.current?.getBoundingClientRect()
    const offsetTop = buttonRef?.current?.offsetTop
    const dropdownTop = props?.topPosition
        ? props?.topPosition
        : boundingClientRect?.y + (props.isSmall ? 1.5 : 0.3) * offsetTop - 0.02 * scrollPosition

    function useOutsideAlerter(ref): void {
        useEffect(() => {
            function handleClickOutside(event): void {
                if (
                    ref.current &&
                    !ref.current.contains(event.target) &&
                    !event.target.classList?.contains('dropdown-area')
                ) {
                    setOpen(false)
                    setSearch('')
                }
            }
            document.addEventListener('mousedown', handleClickOutside)

            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }, [ref])
    }

    const showWindow = (): void => {
        setOpen(!open)
    }

    const clearFilter = (): void => {
        setSearch('')
        setOpen(false)
    }

    const handleSelectAll = (): void => {
        setSelectAll(true)
        setChecked(filterOptionsWithSelected(options, true))
    }
    const handleDeSelectAll = (): void => {
        setSelectAll(false)
        setChecked([])
    }

    const _apply = (): void => {
        if (checked.length === 0) {
            const msg = (
                <WarnMsg>
                    <div style={{ marginBottom: 4 }}>
                        <i className="fas fa-exclamation-triangle"></i>
                    </div>

                    <div>
                        <div className="warn-msg-title">{props?.warning?.title || ''}</div>

                        <div className="warn-msg-subtitle">{props?.warning?.subtitle || ''}</div>
                    </div>
                </WarnMsg>
            )

            globalNotification.notify(msg, 'info', 10, false)
        } else {
            setOpen(false)
            if (apply) {
                setSelectAllApplyed(selectAll)
                apply(checked)
            }
        }
    }

    const cancel = (): void => {
        setSearch('')
        setOpen(false)
        setSelectAll(selectAllApplyed) // is initial state
        if (selectAllApplyed) handleSelectAll()
        else handleDeSelectAll()
    }

    const filterOptionsWithSelected = (opt: FilterOption[], isFromSelectAll?: boolean) => {
        return removeDuplicatesBy(opt, 'title')
            ?.map(option => option?.key)
            .filter(opt => filterApplyed.includes(opt) || isFromSelectAll)
    }

    useEffect(() => {
        if (checked?.length === 0 && (selectAll || options?.length)) {
            setChecked(filterOptionsWithSelected(options))
            if (options.length === filterApplyed.length && options.length > 0) {
                setSelectAll(true)
                setSelectAllApplyed(true)
            }
        }
    }, [options])

    useEffect(() => {
        if (selectAllApplyed && options?.length) {
            setCheckedApplyed(filterOptionsWithSelected(options))
        }
        if (selectAllApplyed && !options?.length) {
            setCheckedApplyed([])
        }
    }, [options])

    function getIntersection(listaCompleta: FilterOption[], filtroLista: string[]): string[] {
        const filtroKeys = new Set(filtroLista)

        return listaCompleta.filter(item => filtroKeys.has(item.key)).map(item => item.title)
    }

    useEffect(() => {
        if (filterApplyed?.length > 0) {
            setCheckedApplyed(getIntersection(removeDuplicatesBy(options, 'title'), filterApplyed))
        }
    }, [filterApplyed])

    return (
        <DropDownFilter className="dropdown-area">
            <button
                style={{ padding: 8 }}
                className={(open ? 'bus-info info openned' : 'bus-info info') + ' dropdown-area'}
                onClick={showWindow}
                ref={buttonRef}
            >
                <DropdownText className="dropdown-area">
                    {selectAllApplyed
                        ? `${i18n('All')} ${checkedApplyed?.length} ${i18n('selected_label')}`
                        : checkedApplyed?.length
                        ? checkedApplyed[0]
                        : ''}
                    {!selectAllApplyed && checkedApplyed?.length > 1 && (
                        <CounterContainer>+{checkedApplyed?.length - 1}</CounterContainer>
                    )}
                    {open ? (
                        <i className="fas fa-caret-up icon-dropdown-selected-vehicle dropdown-area"></i>
                    ) : (
                        <i className="fas fa-caret-down icon-dropdown-selected-vehicle dropdown-area"></i>
                    )}
                </DropdownText>
            </button>
            {open && (
                <ContainerSelect ref={wrapperRef} top={dropdownTop}>
                    <SearchBar>
                        <div style={{ alignItems: 'center' }}>
                            <i className="far fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ border: 'none' }}
                            />
                        </div>
                        <button onClick={clearFilter}>
                            <i className="far fa-times"></i>
                        </button>
                    </SearchBar>
                    <SelectAllContainer>
                        <FlatTree
                            data={[{ title: 'Select All', key: 'select-all' }]}
                            checked={[selectAll ? 'select-all' : '']}
                            callback={() => {}}
                            selectAll={handleSelectAll}
                            deselectAll={handleDeSelectAll}
                        />
                    </SelectAllContainer>
                    <OptionsContainer>
                        <FlatTree
                            data={options?.filter(option =>
                                option?.title?.toLowerCase()?.includes(search?.toLowerCase())
                            )}
                            checked={checked}
                            callback={checkeds => {
                                setChecked(checkeds)
                            }}
                            selectAll={handleSelectAll}
                            deselectAll={handleDeSelectAll}
                        />
                    </OptionsContainer>
                    <WrapperActions>
                        <div>
                            <button onClick={e => _apply()} className="btn btn-primary">
                                {i18n('Apply')}
                            </button>
                        </div>
                        <div>
                            <a
                                href=""
                                onClick={e => {
                                    e.preventDefault()
                                    cancel()
                                }}
                            >
                                {i18n('Cancel')}
                            </a>
                        </div>
                    </WrapperActions>
                </ContainerSelect>
            )}
        </DropDownFilter>
    )
}

const DropdownText = styled.span`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: max-content;

    font-weight: 400;
    color: var(--blue-500);
    font-size: 16px;
`

const CounterContainer = styled.div`
    width: 23px;
    height: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    background: var(--blue-500);
    color: #fff;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    margin: 0 6px;
`

const WrapperActions = styled.div`
    display: flex;
    align-items: center;
    padding: 8px 16px;
    text-align: center;
    border-top: 1px solid ${({ theme }) => theme['gray-300']};
    background: #fff;

    div {
        flex: 1;

        &:first-of-type {
            margin-right: 8px;
        }
    }

    button {
        width: 100%;
        border: none;
    }
`
const FlatTree = ({ data, checked, callback, selectAll, deselectAll }): JSX.Element => {
    return (
        <CustomMenuItem>
            <Tree data={data} checked={checked} callback={callback} selectAll={selectAll} deselectAll={deselectAll} />
        </CustomMenuItem>
    )
}

const CustomMenuItem = styled.div``
export default Dropdown

const SelectAllContainer = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme['gray-300']};
    padding: 4px 0 0 16px;

    div {
        cursor: pointer;
        font-family: var(--font-semibold);
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        font-size: 13px;

        .ant-tree-node-content-wrapper:hover {
            background: ${({ theme }) => theme['gray-0']};
        }
        .ant-tree-node-content-wrapper {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            width: 100%;
            height: 100%;
        }

        .ant-tree .ant-tree-node-content-wrapper.ant-tree-node-selected {
            background-color: ${({ theme }) => theme['gray-0']};
        }
    }
`

const DropDownFilter = styled.div`
    .info {
        background: none;
        border: none;
        padding: 0;
        box-shadow: none;
        cursor: pointer;
        display: flex;
        flex-direction: column;
    }

    .info:hover {
        background: var(--gray-200);
        border-radius: 3px;
    }

    .openned {
        background: var(--blue-200);
    }

    .icon-dropdown-selected-vehicle {
        color: var(--blue-500);
        margin-left: 4px;
    }
`

const ContainerSelect = styled.div`
    position: fixed;
    z-index: 15;
    top: ${({ top }) => `${top}px`};
    margin-left: 0px;
    margin-top: 0px;
    width: 263px;
    background: ${({ theme }) => theme['gray-0']};
    border: 1px solid ${({ theme }) => theme['gray-300']};
    font-family: ${({ theme }) => theme['font-semibold']};
    .Select-value-label div {
        padding: 9px 0;
    }
`

const SearchBar = styled.div`
    display: flex;
    align-items: center !important;
    justify-content: space-between;
    padding: 0 8px;
    border-bottom: 1px solid ${({ theme }) => theme['gray-300']};
    border-radius: 3px 3px 0 0;

    div {
        display: flex;
        align-items: center;
        font-family: var(--font-semibold);
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        font-size: 13px;
    }
    button {
        background: none;
        border: none;
    }

    i {
        color: ${({ theme }) => theme['gray-500']};
    }
`

const OptionsContainer = styled.div`
    overflow-x: hidden;
    overflow-y: auto;
    background: #fff;
    padding: 8px 16px;
    div {
        cursor: pointer;
        font-family: var(--font-semibold);
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        font-size: 13px;
        max-height: 160px;

        .ant-tree-node-content-wrapper:hover {
            background: ${({ theme }) => theme['gray-0']};
        }
        .ant-tree-node-content-wrapper {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            width: 100%;
            height: 100%;
        }
        .ant-tree .ant-tree-node-content-wrapper.ant-tree-node-selected {
            background-color: ${({ theme }) => theme['gray-0']};
        }
    }
`

const WarnMsg = styled.div`
    display: flex;

    div:first-of-type {
        margin-right: 8px;
    }

    .warn-msg-title {
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-semibold']};
        font-size: 16px;
        font-style: normal;
    }

    .warn-msg-subtitle {
        color: ${({ theme }) => theme['gray-800']};
        font-family: ${({ theme }) => theme['font-semibold']};
        font-size: 14px;
        font-style: italic;
    }
`
